import React, { useEffect, useState } from 'react';
import SEO from '../components/SEO';

const ServicesPage = () => {
  const serviceSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    serviceType: 'Technology consulting and custom software development',
    provider: {
      '@type': 'ProfessionalService',
      name: 'Alpha Consulting LLC',
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Atlanta',
        addressRegion: 'GA',
        addressCountry: 'US'
      }
    },
    areaServed: [
      {
        '@type': 'State',
        name: 'Georgia'
      },
      {
        '@type': 'Country',
        name: 'United States'
      }
    ],
    offers: [
      { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Custom software development' } },
      { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Cloud consulting' } },
      { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Application modernization' } },
      { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Automation and AI integration' } }
    ]
  };

  const services = [
    {
      icon: '📱',
      title: 'Web & Mobile Apps',
      description: 'TypeScript, React/Next.js, React Native, Node.js, and robust APIs with performance baked in.',
      color: 'amber',
      details: {
        summary: 'Design and build performant, reliable web and mobile apps with modern stacks and proven delivery practices.',
        bullets: [
          'React/Next.js, React Native, Node.js, PostgreSQL',
          'Design systems, accessibility, and performance budgets',
          'CI/CD, testing, analytics, and observability baked in'
        ]
      }
    },
    {
      icon: '☁️',
      title: 'Cloud & DevOps',
      description: 'AWS, GCP, k8s, Terraform, CI/CD, observability—secure, scalable infra aligned to your roadmap.',
      color: 'green',
      details: {
        summary: 'Infrastructure you can trust: automated, observable, cost‑efficient, and secure by default.',
        bullets: [
          'IaC with Terraform/Bicep, GitOps workflows',
          'Kubernetes, serverless, autoscaling, and caching',
          'Dashboards, logs, metrics, and on‑call playbooks'
        ]
      }
    },
    {
      icon: '🤖',
      title: 'AI & Data Platforms',
      description: 'ETL, analytics, and pragmatic AI integrations for search, copilots, and workflow automation.',
      color: 'teal',
      details: {
        summary: 'Data pipelines and AI features that are useful, reliable, and governed.',
        bullets: [
          'ETL/ELT, warehousing, and semantic search',
          'RAG copilots with evaluation and safety rails',
          'Governance: lineage, access, and retention policies'
        ]
      }
    },
    {
      icon: '🔌',
      title: 'API & Platform',
      description: 'Well‑documented REST/GraphQL, SDKs, auth, rate‑limits, and versioning for smooth integrations.',
      color: 'amber',
      details: {
        summary: 'Treat your API like a product with great DX and rock‑solid reliability.',
        bullets: [
          'REST/GraphQL, Webhooks, and streaming',
          'OAuth/OIDC, keys, quotas, and versioning',
          'Docs portals, SDKs, and change management'
        ]
      }
    },
    {
      icon: '🔄',
      title: 'Migrations & Modernization',
      description: 'Replatform legacy apps, improve reliability, and reduce costs with zero‑downtime strategies.',
      color: 'rose',
      details: {
        summary: 'Lower risk, improve performance, and unlock velocity without disrupting customers.',
        bullets: [
          'Strangler patterns, blue/green, and canary releases',
          'Observability and error budgets to protect SLAs',
          'Cost and performance optimization'
        ]
      }
    },
    {
      icon: '✅',
      title: 'QA & Reliability',
      description: 'Automated tests, performance budgets, and SLOs to keep quality high and outages rare.',
      color: 'yellow',
      details: {
        summary: 'Confidence to ship: automated quality gates and a reliability mindset.',
        bullets: [
          'Unit, integration, and E2E tests with CI',
          'SLOs/SLA, error budgets, chaos and load testing',
          'Release health checks and rollback plans'
        ]
      }
    },
    {
      icon: '🛡️',
      title: 'Security, Policy & Compliance',
      description: 'Risk assessments, secure SDLC, policies, and compliance programs aligned with your industry.',
      color: 'indigo',
      details: {
        summary: 'Pragmatic security woven through your product, people, and processes not bolted on.',
        bullets: [
          'Threat modeling, SDLC controls, and secrets hygiene',
          'Policies and training: access, data handling, BYOD',
          'Compliance readiness (SOC 2, ISO 27001, HIPAA, GDPR)'
        ]
      }
    },
    {
      icon: '🏢',
      title: 'Enterprise Systems',
      description: 'Complex integrations, legacy modernization, and enterprise-grade solutions at scale.',
      color: 'blue',
      details: {
        summary: 'Build and modernize enterprise systems with proven patterns for reliability, security, and compliance.',
        bullets: [
          'ERP/CRM integrations, enterprise service bus, and workflow automation',
          'Legacy system modernization with phased migration strategies',
          'Enterprise architecture, governance, and change management'
        ]
      }
    },
    {
      icon: '🔷',
      title: 'Microservices',
      description: 'Design, build, and deploy resilient distributed systems with domain-driven architecture.',
      color: 'cyan',
      details: {
        summary: 'Break down monoliths and build scalable microservices with clear boundaries and contracts.',
        bullets: [
          'Domain-driven design, API gateways, and service mesh',
          'Event-driven architecture, message queues, and CQRS patterns',
          'Distributed tracing, circuit breakers, and resilience patterns'
        ]
      }
    }
  ];

  const [activeService, setActiveService] = useState(null);

  const openModal = (service) => setActiveService(service);
  const closeModal = () => setActiveService(null);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') closeModal();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <div className="pt-24">
      <SEO 
        title="Technology Consulting Services in Atlanta, GA | Alpha Consulting LLC"
        description="Technology consulting services in Atlanta, Georgia for startups, SMBs, and enterprise teams. We build custom software, cloud platforms, APIs, automation, and modernized business systems."
        keywords="technology consulting services Atlanta GA, custom software development Georgia, Atlanta cloud consulting, enterprise software company Georgia, API development Atlanta, application modernization Georgia"
        url="https://thealphaconsult.com/services"
        image="https://thealphaconsult.com/3ff0f777-6fef-48b9-81ec-9c3949d95efa_3840.png"
        structuredData={serviceSchema}
      />
      <section className="relative py-20 bg-gradient-to-b from-black to-orange-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-amber-300/80 font-nunito">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-400"></span> What we do
            </span>
            <h2 className="mt-3 text-3xl sm:text-4xl tracking-tight font-nunito font-bold">
              Technology consulting services for Atlanta and Georgia companies
            </h2>
            <p className="mt-3 text-orange-300 font-nunito">
              Whether you're launching a SaaS product, modernizing operations, or replacing disconnected systems, our Georgia-based team combines engineering, cloud, automation, and security expertise to deliver software that drives measurable business outcomes.
            </p>
          </div>

          <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {services.map((service, index) => (
              <div 
                key={index}
                className="group relative rounded-2xl border border-white/10 bg-gradient-to-b from-orange-900/60 to-orange-900/30 p-6 hover:border-amber-500/40 transition-all"
              >
                <div className={`text-4xl mb-4`}>
                  {service.icon}
                </div>
                <h3 className="text-lg font-semibold font-nunito">{service.title}</h3>
                <p className="mt-2 text-orange-400 font-nunito">{service.description}</p>
                <button
                  type="button"
                  onClick={() => openModal(service)}
                  className="mt-4 inline-flex items-center text-amber-300/90 group-hover:text-amber-300 transition-colors font-nunito"
                >
                  Learn more
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
                    <path d="M5 12h14M12 5l7 7-7 7"></path>
                  </svg>
                </button>
                <div className="pointer-events-none absolute inset-px rounded-2xl ring-1 ring-inset ring-white/10 group-hover:ring-amber-500/40 transition"></div>
              </div>
            ))}
          </div>

          {/* Modal */}
          {activeService && (
            <div
              className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
              role="dialog"
              aria-modal="true"
              aria-labelledby="service-modal-title"
              onClick={closeModal}
            >
              <div
                className="w-full max-w-2xl rounded-2xl border border-white/10 bg-black/80 shadow-2xl"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-start justify-between p-6 border-b border-white/10">
                  <div className="flex items-center gap-3">
                    <div className="text-2xl">{activeService.icon}</div>
                    <h3 id="service-modal-title" className="text-xl font-semibold font-nunito">
                      {activeService.title}
                    </h3>
                  </div>
                  <button
                    onClick={closeModal}
                    className="p-2 rounded-md hover:bg-white/5 focus:outline-none focus:ring-2 focus:ring-amber-500"
                    aria-label="Close"
                  >
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <path d="M18 6 6 18"></path>
                      <path d="m6 6 12 12"></path>
                    </svg>
                  </button>
                </div>

                <div className="p-6 space-y-4">
                  {activeService.details?.summary && (
                    <p className="text-orange-300 font-nunito">{activeService.details.summary}</p>
                  )}
                  {activeService.details?.bullets && (
                    <ul className="list-disc list-inside space-y-2 text-orange-200 font-nunito">
                      {activeService.details.bullets.map((item, i) => (
                        <li key={i}>{item}</li>
                      ))}
                    </ul>
                  )}
                </div>

                <div className="p-6 pt-0 flex justify-end gap-3">
                  <button
                    onClick={closeModal}
                    className="px-4 py-2 rounded-full bg-white/10 border border-white/10 text-orange-100 hover:bg-white/15 transition font-nunito"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
